import { useProjectParams } from '@hooks/useProjectParams';
import type { CostGroupEnum } from 'gql/graphql';
import { getActKC2StatusColor, getActKC2StatusLabel } from 'pages/Project/Common/types';
import { Badge, Button, IconArrowDown, IconArrowUp } from 'uikit';

import type { ICellRendererParams } from 'ag-grid-community';
import { Link } from 'react-router-dom';

import Styles from './groupNameAndNumberRenderer.module.css';

interface TGroupNameAndNumberRendererProps<TData> extends ICellRendererParams<TData> {
    groupState?: { [key: string]: boolean };
    changeGroupState?: (id: string) => () => void;
    isUseLink?: boolean;
    availableLinkTypes?: CostGroupEnum[];
}

export const GroupNameAndNumberRenderer = <
    TData extends {
        isGroup?: boolean;
        groupId?: string;
        number?: number;
        status?: any;
        actKC2Id?: number;
        actId?: number;
        chapter?: string;
        constructionTypeId?: number;
        workOrderObjectId?: number;
        costGroup: CostGroupEnum;
        isCorrection: boolean;
        fullNumber: number;
    },
>(
    props: TGroupNameAndNumberRendererProps<TData>,
) => {
    const { id: projectId, orderId, methodology } = useProjectParams();

    const { data, groupState, changeGroupState, isUseLink = false, availableLinkTypes } = props;

    const number = data?.isCorrection ? data?.fullNumber : data?.number;
    const actNameStartsWithNumber = number?.toString()?.startsWith('№');
    const actName = `КС-2 ${actNameStartsWithNumber ? number : `${number ? `№${number}` : ''}`}`;

    const localRestrictions =
        data?.costGroup && availableLinkTypes ? availableLinkTypes.includes(data?.costGroup) : true;

    if (data?.isGroup) {
        const currentGroupId = data?.groupId ?? '';

        const areChildrenShown = !!(currentGroupId && groupState?.[currentGroupId]);

        return (
            <>
                <Button
                    size="s"
                    view="clear"
                    onlyIcon
                    iconRight={areChildrenShown ? IconArrowUp : IconArrowDown}
                    onClick={changeGroupState?.(currentGroupId)}
                    style={{ marginRight: '10px', color: 'var(--text-dark-translucent)' }}
                />
                {data?.number}
            </>
        );
    }

    return (
        <div style={{ display: 'flex', alignItems: 'center' }}>
            {data?.status && (
                <Badge
                    minified
                    size="s"
                    label={getActKC2StatusLabel(data?.status)}
                    status={getActKC2StatusColor(data?.status)}
                    className={getActKC2StatusColor(data?.status) === 'progress' ? 'BadgeColor_progress' : undefined}
                    style={{ marginRight: '10px' }}
                />
            )}
            {localRestrictions && isUseLink ? (
                <Link
                    to={`/projects/${methodology}/${projectId}/order/${orderId}`}
                    state={{
                        actNavInfo: {
                            actId: data?.actKC2Id ?? data?.actId,
                            constructionTypeId: data?.constructionTypeId,
                            workOrderObjectId: data?.workOrderObjectId,
                            costGroup: data?.costGroup,
                            chapter: data?.chapter,
                        },
                    }}
                    className={Styles.linkStyle}
                >
                    {actName}
                </Link>
            ) : (
                actName
            )}
        </div>
    );
};

GroupNameAndNumberRenderer.displayName = 'GroupNameAndNumberRenderer';
